import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, ChevronDown, ChevronUp, Upload, Image as ImageIcon } from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor';

export default function DayEditor({ day, dayIndex, onUpdate, onRemove, onImageUpload, canRemove }) {
  const [isExpanded, setIsExpanded] = useState(dayIndex === 0);

  const handleFieldChange = (field, value) => {
    onUpdate(dayIndex, field, value);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await onImageUpload(dayIndex, file);
    }
  };

  return (
    <div className="border-2 border-slate-100 rounded-2xl overflow-hidden bg-white">
      <div 
        className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
        data-testid={`day-header-${dayIndex}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-primary-700 to-primary-600 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
            {day.dayNumber}
          </div>
          <div>
            <span className="font-semibold text-slate-900 block">{day.title || `Day ${day.dayNumber}`}</span>
            {day.description && <span className="text-sm text-slate-500 line-clamp-1">{day.description.substring(0, 50)}...</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canRemove && (
            <Button 
              type="button" 
              variant="ghost" 
              size="sm" 
              onClick={(e) => { e.stopPropagation(); onRemove(dayIndex); }} 
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
              data-testid={`remove-day-${dayIndex}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
        </div>
      </div>

      {isExpanded && (
        <div className="p-6 space-y-5 border-t border-slate-100">
          {/* Day Title */}
          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-2 block">Day Title *</Label>
            <Input 
              value={day.title || ''} 
              onChange={(e) => handleFieldChange('title', e.target.value)} 
              className="h-11 rounded-xl" 
              placeholder="e.g., Arrival in Paradise" 
              data-testid={`day-title-${dayIndex}`}
            />
          </div>

          {/* Description */}
          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-2 block">Day Description / Activities</Label>
            <RichTextEditor
              value={day.description || ''}
              onChange={(html) => handleFieldChange('description', html)}
              placeholder="Describe the day's activities, sightseeing, experiences…"
              minHeight={150}
              testId={`day-description-${dayIndex}`}
            />
            <p className="text-xs text-slate-400 mt-1">Use bullets, headings or bold to format the day plan — the same formatting renders in the PDF.</p>
          </div>

          {/* Day Image */}
          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-2 block flex items-center gap-2">
              <ImageIcon className="h-4 w-4" /> Day Image (Optional - max 1)
            </Label>
            <div className="flex items-center gap-4">
              {day.imageUrl ? (
                <div className="relative w-40 h-28 rounded-xl overflow-hidden border-2 border-slate-200 shadow-sm">
                  <img src={day.imageUrl} alt={`Day ${day.dayNumber}`} className="w-full h-full object-cover" />
                  <Button 
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 h-7 w-7 p-0 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg"
                    onClick={() => handleFieldChange('imageUrl', '')}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <label className="flex items-center justify-center w-40 h-28 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors">
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={handleImageChange} 
                    className="hidden" 
                  />
                  <div className="text-center">
                    <Upload className="h-7 w-7 text-slate-400 mx-auto mb-1" />
                    <span className="text-xs text-slate-500">Upload Image</span>
                  </div>
                </label>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
